/**
 * What the rundown tree shows, as plain view models: the materialised tree
 * (`@norna/domain/tree.js`) flattened into the visible rows of a virtualised
 * list, Segment → Part → Piece, each row reduced to the fields its line
 * displays. Components get these, never a `Row` — they do not learn the
 * replication model (the same rule as `RundownSummary`).
 *
 * **Adlibs** follow the engine's model (`packages/engine/src/model.ts`): an
 * adlib row under a part is that part's adlib, one under the rundown is a
 * global adlib. A part lists its pieces, then its adlibs; the global adlibs
 * come after the last segment. Each kind is its own group, ordered by `ord`
 * among its own kind (`drop.ts` moves them the same way). Rows the
 * materialiser put in "Lost & found" (orphans, cut cycles — PLAN §6.2) are
 * listed after the rundown under a header, so nothing a user wrote is ever
 * silently invisible.
 */
import { readFields } from '@norna/domain/fields.js';
import type { Row } from '@norna/domain/row.js';
import { childrenOf, LOST_AND_FOUND_KEY, type RowTree } from '@norna/domain/tree.js';
import { pieceTypeShort } from './pieceTypes.js';

export type TreeKind = 'segment' | 'part' | 'piece' | 'adlib';

export type SegmentView = {
  readonly kind: 'segment';
  readonly name: string;
  readonly budgetDuration: number | null;
  readonly hidden: boolean;
  /** Sum of the expected durations of its timed parts (untimed ones are not counted, PLAN §5.1). */
  readonly totalDuration: number;
  readonly partCount: number;
};

export type PartView = {
  readonly kind: 'part';
  readonly title: string;
  readonly expectedDuration: number | null;
  readonly autoNext: boolean;
  readonly untimed: boolean;
  readonly floated: boolean;
  readonly invalid: string | null;
  readonly pieceCount: number;
  readonly hasScript: boolean;
};

export type PieceView = {
  readonly kind: 'piece';
  readonly name: string;
  readonly pieceType: string;
  readonly typeShort: string;
  readonly sourceLayer: string;
  readonly start: number | 'now';
  readonly duration: number | null;
};

export type AdlibView = {
  readonly kind: 'adlib';
  readonly name: string;
  readonly pieceType: string;
  readonly typeShort: string;
  readonly sourceLayer: string;
  /** How long it runs once played; null = until something ends it. */
  readonly expectedDuration: number | null;
  /** Under the rundown (any part may play it), not a part. */
  readonly global: boolean;
};

export type RowView = SegmentView | PartView | PieceView | AdlibView;

export type FlatRow = {
  readonly id: string;
  readonly parentId: string | null;
  readonly depth: 0 | 1 | 2;
  readonly view: RowView;
  readonly hasChildren: boolean;
  readonly collapsed: boolean;
  /** Under the "Lost & found" header, not the rundown. */
  readonly lost: boolean;
};

export type FlatTree = { readonly rows: readonly FlatRow[]; readonly lostCount: number };

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** A row's fields in their typed view; a row whose stored data fails its schema is shown with what can be read. */
function fieldsOf(row: Row): Readonly<Record<string, unknown>> {
  const read = readFields(row.kind, row.f);
  return read.ok ? read.value.fields : row.f;
}

function numOrNull(value: unknown): number | null {
  return typeof value === 'number' ? value : null;
}

function invalidReason(row: Row, f: Readonly<Record<string, unknown>>): string | null {
  const read = readFields(row.kind, row.f);
  if (!read.ok) return read.error;
  if (f['invalid'] !== true) return null;
  const reason = str(f['invalidReason']);
  return reason === '' ? 'invalid' : reason;
}

export function partView(row: Row, pieceCount: number): PartView {
  const f = fieldsOf(row);
  return {
    kind: 'part',
    title: str(f['title']),
    expectedDuration: numOrNull(f['expectedDuration']),
    autoNext: f['autoNext'] === true,
    untimed: f['untimed'] === true,
    floated: f['floated'] === true,
    invalid: invalidReason(row, f),
    pieceCount,
    hasScript: str(f['script']).trim() !== '',
  };
}

export function pieceView(row: Row): PieceView {
  const f = fieldsOf(row);
  const enable = f['enable'];
  const start = typeof enable === 'object' && enable !== null ? Reflect.get(enable, 'start') : 0;
  const duration = typeof enable === 'object' && enable !== null ? Reflect.get(enable, 'duration') : null;
  const pieceType = str(f['pieceType']);
  return {
    kind: 'piece',
    name: str(f['name']),
    pieceType,
    typeShort: pieceTypeShort(pieceType),
    sourceLayer: str(f['sourceLayer']),
    start: start === 'now' ? 'now' : typeof start === 'number' ? start : 0,
    duration: numOrNull(duration),
  };
}

export function adlibView(row: Row, global: boolean): AdlibView {
  const f = fieldsOf(row);
  const pieceType = str(f['pieceType']);
  return {
    kind: 'adlib',
    name: str(f['name']),
    pieceType,
    typeShort: pieceTypeShort(pieceType),
    sourceLayer: str(f['sourceLayer']),
    expectedDuration: numOrNull(f['expectedDuration']),
    global,
  };
}

export function segmentView(row: Row, parts: readonly PartView[]): SegmentView {
  const f = fieldsOf(row);
  return {
    kind: 'segment',
    name: str(f['name']),
    budgetDuration: numOrNull(f['budgetDuration']),
    hidden: f['hidden'] === true,
    totalDuration: parts.reduce((sum, p) => sum + (p.untimed ? 0 : (p.expectedDuration ?? 0)), 0),
    partCount: parts.length,
  };
}

function isTreeKind(kind: string): kind is TreeKind {
  return kind === 'segment' || kind === 'part' || kind === 'piece' || kind === 'adlib';
}

type Emit = (row: FlatRow) => void;

function emitPart(tree: RowTree, part: Row, lost: boolean, collapsed: ReadonlySet<string>, emit: Emit, depth: 0 | 1): PartView {
  const children = childrenOf(tree, part.rowId);
  const pieces = children.filter((r) => r.kind === 'piece');
  const adlibs = children.filter((r) => r.kind === 'adlib');
  const view = partView(part, pieces.length);
  const isCollapsed = collapsed.has(part.rowId);
  emit({ id: part.rowId, parentId: part.parentId, depth, view, hasChildren: pieces.length + adlibs.length > 0, collapsed: isCollapsed, lost });
  if (!isCollapsed) {
    const childDepth = depth === 0 ? 1 : 2;
    for (const piece of pieces) emit({ id: piece.rowId, parentId: piece.parentId, depth: childDepth, view: pieceView(piece), hasChildren: false, collapsed: false, lost });
    for (const adlib of adlibs) emit({ id: adlib.rowId, parentId: adlib.parentId, depth: childDepth, view: adlibView(adlib, false), hasChildren: false, collapsed: false, lost });
  }
  return view;
}

function emitSegment(tree: RowTree, segment: Row, lost: boolean, collapsed: ReadonlySet<string>, emit: Emit): void {
  const isCollapsed = collapsed.has(segment.rowId);
  const parts = childrenOf(tree, segment.rowId).filter((r) => r.kind === 'part');
  const buffered: FlatRow[] = [];
  const partViews = parts.map((p) => emitPart(tree, p, lost, collapsed, (r) => buffered.push(r), 1));
  emit({ id: segment.rowId, parentId: segment.parentId, depth: 0, view: segmentView(segment, partViews), hasChildren: parts.length > 0, collapsed: isCollapsed, lost });
  if (!isCollapsed) for (const r of buffered) emit(r);
}

/**
 * The visible rows, in display order: the rundown's segments (its direct
 * children in the tree), each followed by its parts and their pieces and
 * adlibs unless collapsed; then the global adlibs; then the lost & found rows
 * at their own kinds' depths.
 */
export function flattenTree(tree: RowTree, rundownId: string, collapsed: ReadonlySet<string>): FlatTree {
  const rows: FlatRow[] = [];
  const emit: Emit = (r) => rows.push(r);
  for (const segment of childrenOf(tree, rundownId)) {
    if (segment.kind === 'segment') emitSegment(tree, segment, false, collapsed, emit);
  }
  for (const adlib of childrenOf(tree, rundownId)) {
    if (adlib.kind === 'adlib') emit({ id: adlib.rowId, parentId: adlib.parentId, depth: 0, view: adlibView(adlib, true), hasChildren: false, collapsed: false, lost: false });
  }
  const lost = childrenOf(tree, LOST_AND_FOUND_KEY).filter((r) => isTreeKind(r.kind));
  for (const row of lost) {
    if (row.kind === 'segment') emitSegment(tree, row, true, collapsed, emit);
    else if (row.kind === 'part') emitPart(tree, row, true, collapsed, emit, 0);
    else emit({ id: row.rowId, parentId: row.parentId, depth: 0, view: row.kind === 'adlib' ? adlibView(row, false) : pieceView(row), hasChildren: false, collapsed: false, lost: true });
  }
  return { rows, lostCount: lost.length };
}

/** The name a row goes by in a toast or a confirmation ("Delete “Weather”?"). */
export function displayName(view: RowView): string {
  const name = view.kind === 'part' ? view.title : view.name;
  return name === '' ? `(untitled ${view.kind})` : name;
}
